import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Popup } from '_atoms';
import { BreadCrumbs } from '_atoms/BreadCrumbs';
import { JobHeader, PopupReassignRecruiter, StepsEmpty, PopupCloseDeclineOpening } from '_molecules';
import { JobStepsTab } from '_organisms';
import { JobDescriptionTab, JobOpeningsTab, JobHiredTab, JobPromotionRequestsTab, AccessDenied } from '_templates';
import { setStepsToJob } from 'store/reducers/jobReducer';
import { setLoading } from 'store/reducers/commonReducer';
import { getUser } from 'store/selectors/authorizeSelectors';
import { getHrManagers, getLocations, getStepsLoading } from 'store/selectors/commonSelectors';
import {
  getJobApprovers,
  getJobCandidates,
  getJobCVS,
  getJobCVSLinks,
  getJobData,
  getJobLoader,
  getJobOffering,
  getJobProbationPeriod,
  getJobScorecard,
  getJobSteps,
  getJobUserData,
  getOpeningCandidate,
  getOpeningsList,
  getRooms,
} from 'store/selectors/jobSelectors';
import history from 'helpers/history';
import * as actions from 'actions';
import { MixpanelService } from 'helpers/Mixpanel';
import { BUTTON_TEXT, POPUP_BODY, POPUP_LABELS } from 'constants/text';
import { JOB_STATUSES } from 'constants/job';
import { AbilityContext, ACTIONS, SUBJECTS, UI } from 'permission';
import { OPENING_STATUSES } from 'constants/opening';
import type { AppDispatch, RootState } from 'store';
import type { JobInfo, JobUserData, JobWorkflowStep, Opening } from 'store/reducers/jobReducer';
import type { Uuid } from 'store/reducers/types';
import './jobs.scss';

interface RecruiterRef {
  uuid: Uuid;
  full_name: string;
  avatar?: string | null;
  is_active_responsible_recruiter?: boolean;
}

interface OwnProps {
  id: Uuid;
  tab?: string;
  step?: Uuid;
  candidate?: Uuid;
}

interface ViewJobState {
  teamLead: string;
  name: string;
  confirmationPopup: boolean;
  service_info: unknown;
  openDeletePopup: boolean;
  openArchivePopup: boolean;
  openUnarchivePopup: boolean;
  selectedRR: Partial<RecruiterRef>;
  selectedMR: RecruiterRef[];
  modeApprovers: boolean;
  idToEdit: Uuid | null;
  openDeclinePopup: Opening | undefined;
  last_closed_at: string | null;
  last_archived_at: string | null;
  disabled: boolean;
  confirmAssignToYou: boolean;
  activeStep: number;
  showRecruiters: boolean;
  isActiveRecruiter: boolean;
  isActiveRecruiterUuid: Partial<RecruiterRef>;
  newRecruiters: Uuid[];
  confirmationDeletingRRPopup: boolean;
  disableOpeningActions: boolean;
  allOpeningsList: Opening[];
  openingLoading: boolean;
  accessDenied?: boolean;
  openingsFilter?: Record<string, unknown>;
}

const initialsState: ViewJobState = {
  teamLead: '',
  name: '',
  confirmationPopup: false,
  service_info: null,
  openDeletePopup: false,
  openArchivePopup: false,
  openUnarchivePopup: false,
  selectedRR: {},
  selectedMR: [],
  modeApprovers: false,
  idToEdit: null,
  openDeclinePopup: undefined,
  last_closed_at: null,
  last_archived_at: null,
  disabled: false,
  confirmAssignToYou: false,
  activeStep: 0,
  showRecruiters: false,
  isActiveRecruiter: false,
  isActiveRecruiterUuid: {},
  newRecruiters: [],
  confirmationDeletingRRPopup: false,
  disableOpeningActions: false,
  allOpeningsList: [],
  openingLoading: true,
};

function mapStateToProps(store: RootState) {
  return {
    cvsLinks: getJobCVSLinks(store),
    user: getUser(store),
    author: getUser(store),
    jobData: getJobData(store),
    openingsList: getOpeningsList(store),
    steps: getJobSteps(store),
    userData: getJobUserData(store),
    candidates: getJobCandidates(store),
    approversCandidate: getJobApprovers(store),
    cvs: getJobCVS(store),
    scorecard: getJobScorecard(store),
    offeringCandidate: getJobOffering(store),
    probationPeriod: getJobProbationPeriod(store),
    activityList: store.job.activityList,
    openingCandidate: getOpeningCandidate(store),
    steps_loading: getStepsLoading(store),
    rooms: getRooms(store),
    loader: getJobLoader(store),
    hr_managers: getHrManagers(store),
    locations: getLocations(store),
  };
}

function mapDispatchToProps(dispatch: AppDispatch) {
  return {
    getApprovers: bindActionCreators(actions.getApprovers, dispatch),
    getJobInfo: ((uuid: Uuid, clearData?: boolean) => dispatch(actions.getJobInfo(uuid, clearData))) as (
      uuid: Uuid,
      clearData?: boolean
    ) => Promise<JobInfo | null | undefined>,
    clearJobInfo: bindActionCreators(actions.clearJobInfo, dispatch),
    deleteJobs: bindActionCreators(actions.deleteJobs, dispatch),
    assignHrToJobs: ((uuid: Uuid, recruiters: Uuid[], assignedAsRecruiters?: Uuid[]) =>
      dispatch(actions.assignHrToJobs(uuid, recruiters, assignedAsRecruiters))) as (
      uuid: Uuid,
      recruiters: Uuid[],
      assignedAsRecruiters?: Uuid[]
    ) => Promise<void>,
    setLoading: bindActionCreators(setLoading, dispatch),
    getSteps: ((id: Uuid, updateOnlySteps?: boolean) => dispatch(actions.getSteps(id, updateOnlySteps))) as (
      id: Uuid,
      updateOnlySteps?: boolean
    ) => Promise<JobWorkflowStep[] | null | undefined>,
    getCandidateStepAssignment: ((
      jobId: Uuid,
      candidateId: Uuid,
      clearData: boolean,
      steps: JobWorkflowStep[],
      step: number
    ) => dispatch(actions.getCandidateStepAssignment(jobId, candidateId, clearData, steps, step))) as (
      jobId: Uuid,
      candidateId: Uuid,
      clearData: boolean,
      steps: JobWorkflowStep[],
      step: number
    ) => Promise<void>,
    getCandidatesJobs: bindActionCreators(actions.getCandidatesJobs, dispatch) as unknown as (
      ...args: unknown[]
    ) => Promise<{ results: unknown[]; count: number } | null | undefined>,
    getOpenings: bindActionCreators(actions.getOpenings, dispatch),
    setDataOpening: bindActionCreators(actions.setDataOpening, dispatch),
    deleteOpening: bindActionCreators(actions.deleteOpening, dispatch),
    approveDeclineOpening: ((
      jobId: Uuid,
      openingId: Uuid | null,
      action: string,
      body: Record<string, unknown>,
      filters?: Record<string, unknown>
    ) => dispatch(actions.approveDeclineOpening(jobId, openingId as Uuid, action, body, filters))) as (
      jobId: Uuid,
      openingId: Uuid | null,
      action: string,
      body: Record<string, unknown>,
      filters?: Record<string, unknown>
    ) => Promise<void>,
    reopenOpening: ((jobId: Uuid, openingId: Uuid, body: Record<string, unknown>, filters?: Record<string, unknown>) =>
      dispatch(actions.reopenOpening(jobId, openingId, body, filters))) as (
      jobId: Uuid,
      openingId: Uuid,
      body: Record<string, unknown>,
      filters?: Record<string, unknown>
    ) => Promise<void>,
    getProbationPassedStartDate: bindActionCreators(actions.getProbationPassedStartDate, dispatch),
    getProbationPassedScorecard: bindActionCreators(actions.getProbationPassedScorecard, dispatch),
    getActivityList: bindActionCreators(actions.getActivityList, dispatch),
    getOpening: bindActionCreators(actions.getOpening, dispatch),
    changeStateJob: ((jobId: Uuid, body: Record<string, unknown>) => dispatch(actions.changeStateJob(jobId, body))) as (
      jobId: Uuid,
      body: Record<string, unknown>
    ) => Promise<unknown>,
    setStepsToJob: bindActionCreators(setStepsToJob, dispatch),
    getRoomsOptions: bindActionCreators(actions.getRoomsOption, dispatch),
    getListHrManagers: bindActionCreators(actions.getListHrManagers, dispatch),
    reassignResponsibleRecruiter: ((
      body: { job: Uuid; candidate: Uuid; [key: string]: unknown },
      steps: JobWorkflowStep[],
      updateInterview: boolean,
      updateStepFullAssignment: boolean,
      step: number
    ) =>
      dispatch(actions.reassignResponsibleRecruiter(body, steps, updateInterview, updateStepFullAssignment, step))) as (
      body: { job: Uuid; candidate: Uuid; [key: string]: unknown },
      steps: JobWorkflowStep[],
      updateInterview: boolean,
      updateStepFullAssignment: boolean,
      step: number
    ) => Promise<void>,
    reassignRecruiterWithActiveCandidates: ((body: Record<string, unknown>, jobId: Uuid) =>
      dispatch(actions.reassignRecruiterWithActiveCandidates(body, jobId))) as (
      body: Record<string, unknown>,
      jobId: Uuid
    ) => Promise<unknown>,
    getListLocations: bindActionCreators(actions.getListLocations, dispatch),
    getTodaysInterviews: bindActionCreators(actions.getTodaysInterviewsAPI, dispatch),
  };
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = OwnProps & PropsFromRedux;

class ViewJob extends Component<Props, ViewJobState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      ...initialsState,
    };
    this.showPopupDelete = this.showPopupDelete.bind(this);
    this.selectRR = this.selectRR.bind(this);
    this.approveRR = this.approveRR.bind(this);
    this.declineOpening = this.declineOpening.bind(this);
    this.approveDeclineOpening = this.approveDeclineOpening.bind(this);
    this.showDeclinePopup = this.showDeclinePopup.bind(this);
    this.selectMultipleRecruiters = this.selectMultipleRecruiters.bind(this);
    this.updateData = this.updateData.bind(this);
    this.approveMultipleRecruiters = this.approveMultipleRecruiters.bind(this);
    this.deleteRecruiter = this.deleteRecruiter.bind(this);
    this.reassignRecruiter = this.reassignRecruiter.bind(this);
    this.unassignRecruitiers = this.unassignRecruitiers.bind(this);
    this.showPopupArchive = this.showPopupArchive.bind(this);
    this.showPopupUnarchive = this.showPopupUnarchive.bind(this);
  }

  componentDidMount() {
    this.props.setLoading(true);
    this.props.getJobInfo(this.props.id).catch(() => {
      this.setState({ accessDenied: true });
    });
    actions.getOpeningsList(this.props.id).then(ops => {
      this.setState({ allOpeningsList: ops ?? [], openingLoading: false });
    });
    this.props.getSteps(this.props.id).then(steps => {
      if (this.props.tab !== 'steps' || this.props.step || this.props.candidate) return;
      let needActionsStep =
        steps?.findIndex(step => (step as JobWorkflowStep & { need_actions?: boolean }).need_actions) ?? -1;
      if (needActionsStep < 0) needActionsStep = 0;
      if (this.props.steps[needActionsStep]) {
        history.push(
          `/jobs/${this.props.id}/show/steps/${this.props.steps[needActionsStep].uuid}${
            this.props.candidate ? `/${this.props.candidate}` : ''
          }`
        );
      }
    });
    const mixpanel = new MixpanelService();
    mixpanel.track('View Page.Job Details', {
      'Job ID': this.props.id,
    });
  }

  componentWillUnmount() {
    this.props.setStepsToJob([]);
    this.props.clearJobInfo();
  }

  deleteRecruiter(item: RecruiterRef) {
    const jobData = this.props.jobData as JobInfo;
    const newRecruiters = [
      ...((jobData.recruiter_leads as RecruiterRef[] | undefined) ?? []),
      ...(jobData.recruiters ?? []),
    ]
      .filter(i => i.uuid !== item.uuid)
      .map(i => i.uuid);
    if (item.is_active_responsible_recruiter) {
      this.setState({
        isActiveRecruiter: true,
        isActiveRecruiterUuid: { uuid: item.uuid, full_name: item.full_name, avatar: item.avatar },
      });
    } else {
      this.setState({ confirmationDeletingRRPopup: true });
      this.setState({ newRecruiters });
    }
  }

  unassignRecruitiers(reassignRecruiters: Record<string, { value: Uuid }>) {
    const jobData = this.props.jobData as JobInfo;
    this.props
      .reassignRecruiterWithActiveCandidates(
        {
          reassign_recruiters: Object.entries(reassignRecruiters).map(([key, v]) => ({
            old_recruiter: key,
            new_recruiter: v.value,
          })),
          job: jobData.uuid,
        },
        jobData.uuid
      )
      .then(() => {
        this.props.getTodaysInterviews();
        if (this.props.tab === 'steps' && this.props.candidate) {
          const stepNumber = this.props.steps.findIndex(({ uuid }) => uuid === this.props.step);
          this.props.getCandidateStepAssignment(
            this.props.id,
            this.props.candidate,
            false,
            this.props.steps,
            stepNumber
          );
        }
      });
    this.setState({ isActiveRecruiter: false });
  }

  showPopupDelete(value: boolean) {
    this.setState({
      openDeletePopup: value,
    });
  }

  showPopupArchive(value: boolean) {
    this.setState({
      openArchivePopup: value,
    });
  }

  showPopupUnarchive(value: boolean) {
    this.setState({
      openUnarchivePopup: value,
    });
  }

  selectRR(item: RecruiterRef) {
    this.setState({
      selectedRR: item,
    });
  }

  selectMultipleRecruiters(item: RecruiterRef | RecruiterRef[]) {
    const { selectedMR } = this.state;
    if (Array.isArray(item)) {
      this.setState({ selectedMR: item });
      return;
    }
    const index = selectedMR.findIndex(el => el.uuid === item.uuid);
    if (index !== -1) this.setState({ selectedMR: [...selectedMR.filter(el => el.uuid !== item.uuid)] });
    else this.setState(prev => ({ selectedMR: [...prev.selectedMR, item] }));
  }

  approveMultipleRecruiters() {
    const jobData = this.props.jobData as JobInfo;
    const arr: Uuid[] = [];
    (jobData.recruiters ?? []).forEach(i => arr.push(i.uuid));
    this.state.selectedMR.forEach(i => !arr.includes(i.uuid) && arr.push(i.uuid));
    this.props.assignHrToJobs(jobData.uuid, arr);
  }

  reassignRecruiter() {
    const jobData = this.props.jobData as JobInfo;
    const arr: Uuid[] = [];
    this.state.selectedMR.forEach(i => arr.push(i.uuid));
    this.props.assignHrToJobs(jobData.uuid, arr);
  }

  approveRR(activeStep: number) {
    this.props
      .reassignResponsibleRecruiter(
        {
          candidate: this.props.userData.uuid,
          job: this.props.jobData.uuid,
          recruiter: this.state.selectedRR.uuid,
        },
        this.props.steps,
        true,
        false,
        activeStep
      )
      .then(() => {
        this.props.getCandidateStepAssignment(this.props.id, this.props.candidate, false, this.props.steps, activeStep);
      });
  }

  declineOpening(reason: string, opening: Opening) {
    const declinePromise = [OPENING_STATUSES.RM_REAPPROVAL, OPENING_STATUSES.LM_REAPPROVAL].includes(
      opening.status as typeof OPENING_STATUSES.RM_REAPPROVAL
    )
      ? this.props.reopenOpening(
          this.props.id,
          opening.uuid,
          { positive: false, comment: reason },
          this.state.openingsFilter
        )
      : this.props.approveDeclineOpening(
          this.props.id,
          this.state.idToEdit,
          'decline',
          { reason },
          this.state.openingsFilter
        );
    declinePromise.finally(() => {
      this.props.getJobInfo(this.props.id);
    });
    this.showDeclinePopup(false);
  }

  updateData(field: string, value: unknown) {
    this.setState({ [field]: value } as Pick<ViewJobState, keyof ViewJobState>);
  }

  approveDeclineOpening(opening: Opening, isDecline: boolean, filters: Record<string, unknown>) {
    if (isDecline) {
      this.setState({ openingsFilter: filters });
      this.showDeclinePopup(opening);
    } else {
      if (this.state.disableOpeningActions) return;
      this.setState({ disableOpeningActions: true });
      const approvePromise = [OPENING_STATUSES.RM_REAPPROVAL, OPENING_STATUSES.LM_REAPPROVAL].includes(
        opening.status as typeof OPENING_STATUSES.RM_REAPPROVAL
      )
        ? this.props.reopenOpening(this.props.id, opening.uuid, { positive: true }, filters)
        : this.props.approveDeclineOpening(this.props.id, opening.uuid, 'approve', {}, filters);
      approvePromise.finally(() => {
        this.props.getJobInfo(this.props.id).finally(() => {
          this.setState({ disableOpeningActions: false });
        });
      });
    }
  }

  showDeclinePopup(opening?: Opening | false) {
    this.setState({
      openDeclinePopup: opening || undefined,
      idToEdit: opening ? opening.uuid : null,
    });
  }

  render() {
    const jobData = this.props.jobData as JobInfo;
    const isArchived = jobData.status === JOB_STATUSES.ARCHIVED;
    const showAddOpening = !isArchived && this.context.can(ACTIONS.CREATE, SUBJECTS.OPENING);
    const showDelete =
      !this.state.openingLoading &&
      !this.props.steps.reduce(
        (sum, step) => sum + Number((step as JobWorkflowStep & { need_actions?: number }).need_actions),
        0
      ) &&
      !this.state.allOpeningsList.find(
        ({ status }) =>
          ![OPENING_STATUSES.CLOSED, OPENING_STATUSES.DECLINED].includes(status as typeof OPENING_STATUSES.CLOSED)
      );
    const showArchive =
      !this.state.openingLoading &&
      jobData.status === JOB_STATUSES.OPEN &&
      !this.state.allOpeningsList.find(
        ({ status }) =>
          ![OPENING_STATUSES.CLOSED, OPENING_STATUSES.DECLINED, OPENING_STATUSES.HIRED].includes(
            status as typeof OPENING_STATUSES.CLOSED
          )
      );

    if (this.state.accessDenied) return <AccessDenied />;

    if (this.props.tab === 'promotion-requests' && !this.context.can(ACTIONS.READ, UI.PROMOTION_REQUEST_VIEW)) {
      return <AccessDenied />;
    }

    return (
      <>
        <div className="wrapper-content job-view">
          <BreadCrumbs element="Jobs" elementName={jobData.name} path="/jobs" />
          <JobHeader
            name={jobData.name}
            service={jobData.service}
            status={jobData.status}
            recruiters={jobData.recruiters || []}
            recruiterLeads={(jobData.recruiter_leads as RecruiterRef[]) || []}
            teamLead={jobData.team_lead}
            resourceManager={jobData.resource_manager as RecruiterRef | undefined}
            showAddOpening={showAddOpening}
            showArchive={showArchive}
            showDelete={showDelete}
            showPopupArchive={this.showPopupArchive}
            showPopupUnarchive={this.showPopupUnarchive}
            showPopupDelete={this.showPopupDelete}
            activeTab={this.props.tab}
            onTabClick={value => {
              if (value === this.props.tab) return;
              if (value === 'steps') {
                this.props.getSteps(this.props.id).then(steps => {
                  let needActionsStep =
                    steps?.findIndex(step => (step as JobWorkflowStep & { need_actions?: boolean }).need_actions) ?? -1;
                  if (needActionsStep < 0) needActionsStep = 0;
                  history.push(`/jobs/${this.props.id}/show/steps/${this.props.steps[needActionsStep].uuid}`);
                });
              } else {
                history.push(`/jobs/${this.props.id}/show/${value}`);
              }
            }}
            deleteRecruiter={this.deleteRecruiter}
          />
          {jobData.is_assignments_exists === false && this.props.tab === 'steps' && (
            <StepsEmpty
              showPopupUnarchive={this.showPopupUnarchive}
              jobStatus={jobData.status}
              recruiters={jobData.recruiters || []}
              recruiterLeads={(jobData.recruiter_leads as RecruiterRef[]) || []}
            />
          )}
          {this.props.tab === 'steps' && !!this.props.steps.length && jobData.is_assignments_exists && (
            <JobStepsTab
              activeStep={this.props.step}
              steps_loading={this.props.steps_loading}
              activityList={this.props.activityList}
              selectRR={this.selectRR}
              recruiter={jobData.recruiter as RecruiterRef | undefined}
              selectedRR={this.state.selectedRR}
              jobData={jobData}
              approveRR={this.approveRR}
              steps={this.props.steps}
              userData={this.props.userData as JobUserData & { uuid: Uuid }}
              getCandidateStepAssignment={this.props.getCandidateStepAssignment}
              candidates={this.props.candidates}
              candidate={this.props.candidate}
              id={this.props.id}
              getCandidatesJobs={this.props.getCandidatesJobs}
              getSteps={this.props.getSteps}
              approversCandidate={this.props.approversCandidate}
              cvs={this.props.cvs}
              cvsLinks={this.props.cvsLinks}
              author={this.props.author}
              scorecard={this.props.scorecard}
              openingsList={this.props.openingsList}
              offeringCandidate={this.props.offeringCandidate}
              probationPeriod={this.props.probationPeriod}
              getOpenings={body => this.props.getOpenings(this.props.id, body as Record<string, unknown>)}
              openingCandidate={this.props.openingCandidate}
              rooms={this.props.rooms}
              updateData={this.updateData}
              getActivityList={this.props.getActivityList}
            />
          )}
          {this.props.tab === 'description' && (
            <JobDescriptionTab
              createdAt={jobData.created_at as string | undefined}
              lastOpenedAt={jobData.last_opened_at as string | undefined}
              lastArchivedBy={
                jobData.last_archived_by as { full_name?: string; avatar?: string | null } | null | undefined
              }
              lastArchivedAt={jobData.last_archived_at as string | undefined}
              teamLead={jobData.team_lead}
              resourceManager={jobData.resource_manager as RecruiterRef | undefined}
              recruiters={jobData.recruiters || []}
              recruiterLeads={(jobData.recruiter_leads as RecruiterRef[]) || []}
              service={jobData.service}
              company={(jobData.company as { name?: string }) || {}}
              description={jobData.description}
              benefits={jobData.benefits}
            />
          )}
          {this.props.tab === 'openings' && (
            <JobOpeningsTab
              showAddOpening={showAddOpening}
              openingsList={this.props.openingsList}
              approveDeclineOpening={this.approveDeclineOpening}
              getListLocations={this.props.getListLocations}
              jobName={jobData.name}
            />
          )}
          {this.props.tab === 'hired' && <JobHiredTab jobName={jobData.name} />}
          {this.props.tab === 'promotion-requests' && <JobPromotionRequestsTab />}
        </div>
        <PopupReassignRecruiter
          open={this.state.isActiveRecruiter}
          onClose={() => this.setState({ isActiveRecruiter: false })}
          onConfirm={this.unassignRecruitiers}
          recruitersReassignFrom={[this.state.isActiveRecruiterUuid as RecruiterRef]}
        />
        <Popup
          open={this.state.confirmAssignToYou}
          title={POPUP_LABELS.ASSIGN_CANDIDATE}
          body={POPUP_BODY.ASSIGN_CANDIDATE}
          onClose={() => {
            this.setState({ confirmAssignToYou: false });
          }}
          closeTxt={BUTTON_TEXT.BACK}
          onConfirm={() => {
            const stepNumber = this.props.steps.findIndex(({ uuid }) => uuid === this.props.step);
            this.props.reassignResponsibleRecruiter(
              {
                candidate: this.props.userData.uuid,
                job: jobData.uuid,
                recruiter: this.props.author.uuid,
              },
              this.props.steps,
              true,
              false,
              stepNumber
            );
            this.setState({ confirmAssignToYou: false });
          }}
        />
        <Popup
          open={this.state.confirmationDeletingRRPopup}
          title={POPUP_LABELS.REMOVE_RECRUITER}
          cancelButtonProps={{
            onClick: () => this.setState({ confirmationDeletingRRPopup: false }),
            children: BUTTON_TEXT.CANCEL,
          }}
          okButtonProps={{
            onClick: () => {
              this.props.assignHrToJobs(jobData.uuid, this.state.newRecruiters);
              this.setState({ confirmationDeletingRRPopup: false });
            },
            children: BUTTON_TEXT.CONFIRM,
          }}
        >
          {POPUP_BODY.REMOVE_RECRUITER}
        </Popup>
        <Popup
          open={this.state.openDeletePopup}
          title={POPUP_LABELS.DELETE_JOB}
          cancelButtonProps={{ onClick: () => this.showPopupDelete(false), children: BUTTON_TEXT.CANCEL }}
          okButtonProps={{
            onClick: () => {
              this.props.deleteJobs(this.props.id);
              this.showPopupDelete(false);
            },
            children: 'Delete job',
          }}
        >
          {POPUP_BODY.DELETE_JOB}
        </Popup>
        <Popup
          open={this.state.openArchivePopup}
          title={POPUP_LABELS.ARCHIVE_JOB}
          cancelButtonProps={{ onClick: () => this.showPopupArchive(false), children: BUTTON_TEXT.CANCEL }}
          okButtonProps={{
            onClick: () => {
              this.showPopupArchive(false);
              this.props.changeStateJob(this.props.id, { status: JOB_STATUSES.ARCHIVED }).then(() => {
                this.props.getJobInfo(this.props.id);
              });
            },
            children: BUTTON_TEXT.ARCHIVE,
          }}
        >
          {POPUP_BODY.ARCHIVE_JOB}
        </Popup>
        <Popup
          open={this.state.openUnarchivePopup}
          title={POPUP_LABELS.UNARCHIVE_JOB}
          cancelButtonProps={{ onClick: () => this.showPopupUnarchive(false), children: BUTTON_TEXT.CANCEL }}
          okButtonProps={{
            onClick: () => {
              this.showPopupUnarchive(false);
              this.props.changeStateJob(this.props.id, { status: JOB_STATUSES.OPEN }).then(() => {
                this.props.getJobInfo(this.props.id);
              });
            },
            children: BUTTON_TEXT.UNARCHIVE,
          }}
        >
          {POPUP_BODY.UNARCHIVE_JOB}
        </Popup>
        <PopupCloseDeclineOpening
          open={!!this.state.openDeclinePopup}
          onClose={() => this.showDeclinePopup()}
          confirm={this.declineOpening}
          jobName={jobData.name}
          opening={this.state.openDeclinePopup}
        />
      </>
    );
  }
}

ViewJob.contextType = AbilityContext;

export default connector(ViewJob);
